
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getProjectById, projects } from '../../../mocks/projects';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';

export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const project = getProjectById(id || '');

  if (!project) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-[#0A0A0A] mb-4">
            {i18n.language === 'fr' ? 'Projet non trouvé' : 'Project not found'}
          </h1>
          <Link
            to="/projects"
            className="text-[#0066FF] hover:underline cursor-pointer"
          >
            {i18n.language === 'fr' ? 'Retour aux projets' : 'Back to projects'}
          </Link>
        </div>
      </div>
    );
  }

  const relatedProjects = projects
    .filter(p => p.category === project.category && p.id !== project.id)
    .slice(0, 3);

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'backend':
        return 'ri-server-line';
      case 'frontend':
        return 'ri-layout-line';
      case 'mobile':
        return 'ri-smartphone-line';
      default:
        return 'ri-code-line';
    }
  };

  const getCategoryLabel = (category: string) => {
    if (i18n.language === 'fr') {
      switch (category) {
        case 'backend':
          return 'Backend';
        case 'frontend':
          return 'Frontend';
        case 'mobile':
          return 'Mobile';
        default:
          return category;
      }
    }
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-20">
        <div className="relative w-full h-[500px] overflow-hidden">
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20"></div>
          
          {/* Back Button */}
          <button
            onClick={() => navigate('/projects')}
            className="absolute top-8 left-8 bg-white/10 backdrop-blur-md text-white px-4 py-2 rounded-full flex items-center gap-2 hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            {i18n.language === 'fr' ? 'Retour' : 'Back'}
          </button>

          {/* Project Info Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-3 mb-4">
                <span className={`${project.statusColor} text-white px-4 py-1.5 rounded-full text-xs font-bold whitespace-nowrap`}>
                  {project.status}
                </span>
                <span className="bg-white/20 backdrop-blur-md text-white px-4 py-1.5 rounded-full text-xs font-medium flex items-center gap-2 whitespace-nowrap">
                  <i className={getCategoryIcon(project.category)}></i>
                  {getCategoryLabel(project.category)}
                </span>
              </div>
              <h1 className="text-3xl md:text-5xl font-black text-white mb-4 leading-tight">
                {project.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm">
                <div className="flex items-center gap-2">
                  <i className="ri-calendar-line"></i>
                  <span>{project.period}</span>
                </div>
                {project.manager && (
                  <div className="flex items-center gap-2">
                    <i className="ri-user-line"></i>
                    <span>{project.manager}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* Description */}
              <div>
                <h2 className="text-2xl font-bold text-[#0A0A0A] mb-6 flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#0066FF]/10 rounded-lg flex items-center justify-center">
                    <i className="ri-file-text-line text-[#0066FF]"></i>
                  </div>
                  {i18n.language === 'fr' ? 'Description du projet' : 'Project Description'}
                </h2>
                <p className="text-gray-600 leading-relaxed text-lg">
                  {project.fullDescription}
                </p>
              </div>

              {/* Features */}
              <div>
                <h2 className="text-2xl font-bold text-[#0A0A0A] mb-6 flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#00FF88]/10 rounded-lg flex items-center justify-center">
                    <i className="ri-checkbox-circle-line text-[#00FF88]"></i>
                  </div>
                  {i18n.language === 'fr' ? 'Fonctionnalités clés' : 'Key Features'}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {project.features.map((feature, index) => (
                    <div
                      key={index}
                      className="bg-[#FAFAF8] rounded-xl p-4 flex items-start gap-3 hover:shadow-md transition-shadow"
                    >
                      <div className="w-6 h-6 bg-[#00FF88] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Challenges */}
              <div>
                <h2 className="text-2xl font-bold text-[#0A0A0A] mb-6 flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#FF6B6B]/10 rounded-lg flex items-center justify-center">
                    <i className="ri-flashlight-line text-[#FF6B6B]"></i>
                  </div>
                  {i18n.language === 'fr' ? 'Défis relevés' : 'Challenges Overcome'}
                </h2>
                <div className="space-y-3">
                  {project.challenges.map((challenge, index) => (
                    <div
                      key={index}
                      className="bg-gradient-to-r from-[#FF6B6B]/5 to-transparent rounded-xl p-4 flex items-start gap-3 border-l-4 border-[#FF6B6B]"
                    >
                      <i className="ri-error-warning-line text-[#FF6B6B] mt-0.5"></i>
                      <span className="text-gray-700">{challenge}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Results */}
              <div>
                <h2 className="text-2xl font-bold text-[#0A0A0A] mb-6 flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#0066FF]/10 rounded-lg flex items-center justify-center">
                    <i className="ri-bar-chart-box-line text-[#0066FF]"></i>
                  </div>
                  {i18n.language === 'fr' ? 'Résultats obtenus' : 'Results Achieved'}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {project.results.map((result, index) => (
                    <div
                      key={index}
                      className="bg-gradient-to-br from-[#0066FF]/5 to-[#0066FF]/10 rounded-xl p-5 hover:shadow-lg transition-all"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-[#0066FF] rounded-lg flex items-center justify-center">
                          <i className="ri-trophy-line text-white"></i>
                        </div>
                        <span className="text-gray-800 font-medium">{result}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Gallery */}
              {project.gallery && project.gallery.length > 0 && (
                <div>
                  <h2 className="text-2xl font-bold text-[#0A0A0A] mb-6 flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#8B5CF6]/10 rounded-lg flex items-center justify-center">
                      <i className="ri-image-line text-[#8B5CF6]"></i>
                    </div>
                    {i18n.language === 'fr' ? 'Galerie' : 'Gallery'}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {project.gallery.map((image, index) => (
                      <div
                        key={index}
                        className="relative w-full h-48 rounded-xl overflow-hidden group cursor-pointer"
                      >
                        <img
                          src={image}
                          alt={`${project.title} - ${index + 1}`}
                          className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors"></div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Sidebar */}
            <div className="space-y-8">
              {/* Technologies */}
              <div className="bg-[#FAFAF8] rounded-2xl p-6">
                <h3 className="text-lg font-bold text-[#0A0A0A] mb-4 flex items-center gap-2">
                  <i className="ri-code-s-slash-line text-[#0066FF]"></i>
                  {i18n.language === 'fr' ? 'Technologies utilisées' : 'Technologies Used'}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {project.tech.map((tech, index) => (
                    <span
                      key={index}
                      className="bg-white text-gray-700 px-4 py-2 rounded-lg text-sm font-medium shadow-sm hover:shadow-md transition-shadow cursor-default whitespace-nowrap"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              {/* Links */}
              {(project.github || project.link) && (
                <div className="bg-[#0A0A0A] rounded-2xl p-6">
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <i className="ri-links-line"></i>
                    {i18n.language === 'fr' ? 'Liens du projet' : 'Project Links'}
                  </h3>
                  <div className="space-y-3">
                    {project.github && (
                      <a
                        href={project.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 bg-white/10 hover:bg-white/20 text-white px-4 py-3 rounded-xl transition-colors cursor-pointer"
                      >
                        <i className="ri-github-fill text-xl"></i>
                        <span className="font-medium">
                          {i18n.language === 'fr' ? 'Voir le code source' : 'View Source Code'}
                        </span>
                        <i className="ri-external-link-line ml-auto"></i>
                      </a>
                    )}
                    {project.link && (
                      <a
                        href={project.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 bg-[#0066FF] hover:bg-[#0055DD] text-white px-4 py-3 rounded-xl transition-colors cursor-pointer"
                      >
                        <i className="ri-external-link-line text-xl"></i>
                        <span className="font-medium">
                          {i18n.language === 'fr' ? 'Voir la documentation' : 'View Documentation'}
                        </span>
                        <i className="ri-arrow-right-line ml-auto"></i>
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Contact CTA */}
              <div className="bg-gradient-to-br from-[#0066FF] to-[#0044CC] rounded-2xl p-6 text-white">
                <h3 className="text-lg font-bold mb-2">
                  {i18n.language === 'fr' ? 'Un projet similaire ?' : 'Similar Project?'}
                </h3>
                <p className="text-white/80 text-sm mb-4">
                  {i18n.language === 'fr'
                    ? 'Discutons de votre projet et voyons comment je peux vous aider.'
                    : 'Let\'s discuss your project and see how I can help.'}
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-white text-[#0066FF] px-5 py-3 rounded-xl font-bold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-mail-line"></i>
                  {i18n.language === 'fr' ? 'Me contacter' : 'Contact Me'}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Projects */}
      {relatedProjects.length > 0 && (
        <section className="py-16 px-6 bg-[#FAFAF8]">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold text-[#0A0A0A] mb-8">
              {i18n.language === 'fr' ? 'Projets similaires' : 'Related Projects'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedProjects.map((relatedProject) => (
                <Link
                  key={relatedProject.id}
                  to={`/projects/${relatedProject.id}`}
                  className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer group"
                >
                  <div className="relative w-full h-48 overflow-hidden">
                    <img
                      src={relatedProject.image}
                      alt={relatedProject.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className={`absolute top-4 right-4 ${relatedProject.statusColor} text-white px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap`}>
                      {relatedProject.status}
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-bold text-[#0A0A0A] mb-2 line-clamp-2">
                      {relatedProject.title}
                    </h3>
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {relatedProject.tech.slice(0, 3).map((tech, i) => (
                        <span
                          key={i}
                          className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs whitespace-nowrap"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 text-[#0066FF] text-sm font-semibold group-hover:gap-3 transition-all">
                      <span>{i18n.language === 'fr' ? 'Voir le projet' : 'View Project'}</span>
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
